import { useState } from 'react';
import type { EPCR } from '@/types';
import { cn } from '@/lib/cn';
import { ChevronDown, ChevronRight, Clock } from 'lucide-react';
import { RuleTag } from '@/components/RuleTag';

/**
 * ResponseDelaysPanel — EMSPCR Foundation v1.0 §2.2.
 *
 * eResponse.08–.12: dispatch / response / scene / transport /
 * turn-around delay categorization. Default is "None" — the panel
 * collapses out of the way until the crew marks that a delay
 * occurred. Per the spec, crews only interact when there was a
 * delay; otherwise the fields export as "None" automatically.
 */

interface Props {
  epcr: EPCR;
  onUpdate: (patch: Partial<EPCR>) => void;
}

const DISPATCH_DELAY_OPTIONS = [
  'Address / location problem',
  'Caller description problem',
  'CAD queue / no unit available',
  'Dispatch distracted',
  'Language barrier',
  'No delay',
];
const RESPONSE_DELAY_OPTIONS = [
  'Awaiting co-response (PD / Fire / HEMS)',
  'Crew at other call',
  'Staffing / crew assembly',
  'Vehicle failure',
  'Weather',
  'Traffic',
  'No delay',
];
const SCENE_DELAY_OPTIONS = [
  'Awaiting law enforcement (scene safety)',
  'Awaiting HEMS',
  'Awaiting additional EMS',
  'Difficult access (patient)',
  'Extrication',
  'Safety at scene',
  'Triage',
  'Crowd / bystanders',
  'Directions / address',
  'Patient evaluation',
  'No delay',
];
const TRANSPORT_DELAY_OPTIONS = [
  'Distance to destination',
  'Traffic',
  'Vehicle failure',
  'Weather',
  'Crew decision (safety)',
  'No delay',
];
const TURNAROUND_DELAY_OPTIONS = [
  'Cleaning / decontamination',
  'Crew rest / rehab',
  'Destination slow TOC',
  'Documentation',
  'Equipment restock',
  'No delay',
];

export function ResponseDelaysPanel({ epcr, onUpdate }: Props) {
  const [expanded, setExpanded] = useState(false);

  const hasAny = [
    epcr.eresponse_dispatch_delays,
    epcr.eresponse_response_delays,
    epcr.eresponse_scene_delays,
    epcr.eresponse_transport_delays,
    epcr.eresponse_turnaround_delays,
  ].some((arr) => (arr?.length ?? 0) > 0 && !arr?.every((v) => v === 'No delay'));

  function toggleIn(field: keyof Pick<EPCR,
    'eresponse_dispatch_delays' | 'eresponse_response_delays' | 'eresponse_scene_delays' |
    'eresponse_transport_delays' | 'eresponse_turnaround_delays'
  >, value: string) {
    const current = new Set((epcr[field] as string[] | undefined) ?? []);
    // "No delay" is mutually exclusive with others
    if (value === 'No delay') {
      if (current.has('No delay')) current.clear();
      else { current.clear(); current.add('No delay'); }
    } else {
      current.delete('No delay');
      if (current.has(value)) current.delete(value);
      else current.add(value);
    }
    onUpdate({ [field]: Array.from(current) } as Partial<EPCR>);
  }

  return (
    <section className="border border-slate-700 bg-slate-900/50">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between border-b border-slate-700 bg-slate-900 px-3 py-1.5 text-left"
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <Clock size={12} className="text-slate-400" />
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-200">
            Response Delays
          </h3>
          <span className={cn(
            'text-[10px] font-mono uppercase tracking-widest',
            hasAny ? 'text-amber-300' : 'text-slate-500',
          )}>
            {hasAny ? 'Delay documented' : 'No delays reported'}
          </span>
        </div>
        <RuleTag
          compact
          sources={[
            { standard: 'NEMSIS', element: 'eResponse.08–.12', usage: 'Required', rationale: 'Delay categorization by phase (dispatch / response / scene / transport / turn-around).' },
          ]}
        />
      </button>
      {expanded && (
        <div className="p-3 space-y-3">
          <p className="text-[11px] text-slate-400 leading-snug">
            Defaults to "No delay" at export time. Select only what actually occurred —
            crews do not need to touch this section on a standard run.
          </p>
          <DelayGroup
            label="Dispatch Delay (eResponse.08)"
            options={DISPATCH_DELAY_OPTIONS}
            selected={epcr.eresponse_dispatch_delays ?? []}
            onToggle={(v) => toggleIn('eresponse_dispatch_delays', v)}
            locked={epcr.locked}
          />
          <DelayGroup
            label="Response Delay (eResponse.09)"
            options={RESPONSE_DELAY_OPTIONS}
            selected={epcr.eresponse_response_delays ?? []}
            onToggle={(v) => toggleIn('eresponse_response_delays', v)}
            locked={epcr.locked}
          />
          <DelayGroup
            label="Scene Delay (eResponse.10)"
            options={SCENE_DELAY_OPTIONS}
            selected={epcr.eresponse_scene_delays ?? []}
            onToggle={(v) => toggleIn('eresponse_scene_delays', v)}
            locked={epcr.locked}
          />
          <DelayGroup
            label="Transport Delay (eResponse.11)"
            options={TRANSPORT_DELAY_OPTIONS}
            selected={epcr.eresponse_transport_delays ?? []}
            onToggle={(v) => toggleIn('eresponse_transport_delays', v)}
            locked={epcr.locked}
          />
          <DelayGroup
            label="Turn-Around Delay (eResponse.12)"
            options={TURNAROUND_DELAY_OPTIONS}
            selected={epcr.eresponse_turnaround_delays ?? []}
            onToggle={(v) => toggleIn('eresponse_turnaround_delays', v)}
            locked={epcr.locked}
          />
        </div>
      )}
    </section>
  );
}

function DelayGroup({
  label, options, selected, onToggle, locked,
}: {
  label: string; options: string[]; selected: string[]; onToggle: (v: string) => void; locked: boolean;
}) {
  const set = new Set(selected);
  const noneSelected = set.has('No delay');
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const isNone = o === 'No delay';
          const on = set.has(o);
          const disabled = locked || (!isNone && noneSelected);
          return (
            <button
              key={o}
              type="button"
              onClick={() => onToggle(o)}
              disabled={disabled}
              className={cn(
                'px-2 py-1 text-[11px] border transition-colors',
                on
                  ? isNone
                    ? 'bg-slate-600 text-white border-slate-400'
                    : 'bg-amber-600 text-white border-amber-400'
                  : 'bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700',
                disabled && !on && 'opacity-40 cursor-not-allowed',
              )}
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
